import {Button, Card, CardBody, CardHeader, Chip, Input, Textarea, Typography} from "@material-tailwind/react";
import React, {useEffect, useState, useCallback, useRef} from "react";
import {submitServiceReport} from "../../../services/report/work";
import {
  submitAdminSupportServiceDocumentation,
  updateAdminSupportServiceDocumentation,
  updateServiceSupport,
} from "../../../services/service/service";
import {getServicePaymentAndSteps} from "../../../services/service/service_step";
import {LoadingOverlay} from "../../system/loading_overlay";
import {Toaster} from "../../../helper/Toaster";
import persianNumberFormat from "../../../helper/PersianNumberFormat";
import Dict from "../../../helper/dict";
import {ServiceReportGrid} from "./grid";
import {CardFooterPaginator} from "../../system/cardFooterPaginator";
import {downloadFile} from "../../../config";
import {PaginateContext} from "../../../context/paginate";
import {Modal} from "../../system/modal";
import {ServiceReportFilterContainer} from "../../shared/filters/service_report_filter_container";
import SelectInput from "../../system/select";
import FileCell from "../../system/fileViewer";
import FileInput from "../../system/fileInput";
import {getPersonsNameAndFamilyByRole} from "../../../services/profile/profile";
import {submitAdminServiceSendPaymentLink} from "../../../services/admin_service/send_payment_link";
import {DEFAULT_PAYMENT_METHOD, PAYMENT_GATEWAY_OPTIONS} from "../../../helper/paymentGateway";
import {getServiceReportPaymentAmounts} from "./paymentAmounts";
import {
  BsCheckCircle,
  BsChevronDown,
  BsChevronUp,
  BsClock,
  BsCreditCard,
  BsPatchCheckFill,
  BsPencilSquare,
  BsPersonPlus,
  BsPlus,
  BsX,
} from "react-icons/bs";

const SUPPORT_ROLE_ID = 5;

const getServiceStatusDisplay = (status) => {
  const statusMap = {
    new: {
      label: "در انتظار تکمیل مدارک",
      className: "bg-indigo-50 text-indigo-700 border-indigo-100",
    },
    waiting: {
      label: "در انتظار پرداخت",
      className: "bg-amber-50 text-amber-700 border-amber-100",
    },
    second_waiting: {
      label: "در انتظار پرداخت مرحله دوم",
      className: "bg-amber-50 text-amber-700 border-amber-100",
    },
    finish: {
      label: "اتمام خدمت",
      className: "bg-emerald-50 text-emerald-700 border-emerald-100",
    },
    cancel: {
      label: "لغو شده",
      className: "bg-rose-50 text-rose-700 border-rose-100",
    },
    payment: {
      label: "پرداخت شده",
      className: "bg-sky-50 text-sky-700 border-sky-100",
    },
  };

  return (
    statusMap[status] || {
      label: status || "-",
      className: "bg-blue-gray-50 text-blue-gray-700 border-blue-gray-100",
    }
  );
};

const getPaymentDisplay = (completedFullPayment) => {
  if (completedFullPayment === true) {
    return {
      label: "دو مرحله‌ای، پرداخت کامل",
      className: "bg-emerald-50 text-emerald-700 border-emerald-100",
    };
  }

  if (completedFullPayment === false) {
    return {
      label: "دو مرحله‌ای، بیعانه پرداخت شد",
      className: "bg-amber-50 text-amber-700 border-amber-100",
    };
  }

  return {
    label: "تک مرحله‌ای، پرداخت کامل",
    className: "bg-sky-50 text-sky-700 border-sky-100",
  };
};

const DetailBadge = ({children, className = ""}) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}
  >
    {children}
  </span>
);

const InfoItem = ({label, value, subValue, valueClassName = ""}) => (
  <div className="min-w-0 border-r-2 border-blue-gray-100 px-3 py-1.5">
    <p className="text-xs font-semibold text-blue-gray-500">{label}</p>

    <p className={`mt-1 truncate text-sm font-semibold text-blue-gray-900 ${valueClassName}`}>
      {value || "-"}
    </p>

    {subValue && <p className="mt-0.5 truncate text-xs text-blue-gray-500">{subValue}</p>}
  </div>
);

const SectionTitle = ({children}) => (
  <div className="flex items-center gap-2 pb-1">
    <span className="h-5 w-1 rounded-full bg-violet-500" />
    <p className="text-sm font-bold text-blue-gray-900">{children}</p>
  </div>
);

const getDocumentStatusColor = (status) => {
  if (status === "accepted") return "teal";
  if (status === "rejected") return "red";
  if (status === "new") return "cyan";
  if (status === "upload") return "purple";
  return "blue-gray";
};

const documentStatusOptions = ["new", "upload", "accepted", "rejected"];

const ServiceDocumentsView = ({documents = [], loading, onEdit}) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-blue-gray-50 bg-blue-gray-50/40 py-6 text-center text-sm text-blue-gray-400">
        در حال دریافت مدارک...
      </div>
    );
  }

  if (!documents.length) {
    return (
      <div className="rounded-lg border border-blue-gray-50 bg-blue-gray-50/40 py-6 text-center text-sm text-blue-gray-400">
        مدرکی برای این خدمت ثبت نشده است
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-blue-gray-100">
      <table className="w-full min-w-[760px] text-right">
        <thead>
          <tr className="bg-blue-gray-50/70 text-xs font-semibold text-blue-gray-600">
            <th className="px-4 py-3 text-center">#</th>
            <th className="px-4 py-3">عنوان</th>
            <th className="px-4 py-3">توضیحات</th>
            <th className="px-4 py-3 text-center">وضعیت</th>
            <th className="px-4 py-3 text-center">فایل</th>
            <th className="px-4 py-3 text-center">عملیات</th>
          </tr>
        </thead>

        <tbody>
          {documents.map((document, index) => {
            const isProofOfWork = document.type === "proof_of_work";
            return (
            <tr
              key={document.id || `${document.title}-${index}`}
              className={`border-t border-blue-gray-50 text-sm text-blue-gray-800 ${
                isProofOfWork ? "bg-teal-50/60 ring-1 ring-inset ring-teal-100" : ""
              }`}
            >
              <td className="px-4 py-3 text-center font-semibold">
                {persianNumberFormat(index + 1, true)}
              </td>
              <td className="px-4 py-3 font-semibold">
                <div className="flex flex-col gap-1">
                  <span>{document.title || "-"}</span>
                  {isProofOfWork &&
                    <span className="inline-flex w-fit items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">
                      <BsPatchCheckFill className="h-3.5 w-3.5" />
                      مدرک اثبات انجام کار
                    </span>
                  }
                </div>
              </td>
              <td className="max-w-md px-4 py-3 text-blue-gray-600">
                {document.description || "-"}
              </td>
              <td className="px-4 py-3 text-center">
                <Chip
                  className="inline-flex justify-center textRight"
                  variant="ghost"
                  value={Dict(document.status)}
                  color={getDocumentStatusColor(document.status)}
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex min-h-[3rem] justify-center">
                  {document.file ? <FileCell fileCode={document.file} /> : "-"}
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={() => onEdit(document)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-gray-200 px-2.5 py-1.5 text-xs font-semibold text-blue-gray-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  <BsPencilSquare className="h-3.5 w-3.5" />
                  ویرایش
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ServiceDocumentAdminForm = ({
  document,
  values,
  setValues,
  saving,
  onSave,
  onCancel,
}) => (
  <div className="rounded-xl border border-blue-gray-100 bg-blue-gray-50/40 p-3">
    <div className="mb-3 flex items-center justify-between gap-3">
      <p className="text-sm font-bold text-blue-gray-800">
        {document ? "ویرایش مدرک خدمت" : "ثبت مدرک جدید"}
      </p>

      <button
        type="button"
        onClick={onCancel}
        className="flex h-7 w-7 items-center justify-center rounded-md text-blue-gray-400 transition-colors hover:bg-white hover:text-blue-gray-700"
        aria-label="بستن فرم مدرک"
      >
        <BsX className="h-4 w-4" />
      </button>
    </div>

    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Input
        variant="standard"
        label="عنوان"
        value={values.title}
        onChange={(event) => setValues((current) => ({...current, title: event.target.value}))}
      />

      <div>
        <label className="mb-1 block text-xs font-semibold text-blue-gray-500">
          وضعیت
        </label>
        <select
          value={values.status}
          onChange={(event) => setValues((current) => ({...current, status: event.target.value}))}
          className="h-10 w-full rounded-lg border border-blue-gray-100 bg-white px-3 text-sm text-blue-gray-800 outline-none transition-colors focus:border-blue-500"
        >
          {documentStatusOptions.map((status) => (
            <option key={status} value={status}>
              {Dict(status)}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <Textarea
          variant="standard"
          label="توضیحات"
          value={values.description}
          onChange={(event) => setValues((current) => ({...current, description: event.target.value}))}
        />
      </div>

      <div className="md:col-span-2">
        <FileInput
          classes="w-full justify-start"
          id={`admin-service-document-${document?.id || "new"}`}
          label={document?.file ? "جایگزینی فایل" : "بارگذاری فایل"}
          fileCode={document?.file || ""}
          onChange={(event) =>
            setValues((current) => ({...current, file: event.target.files?.[0] || null}))
          }
        />
      </div>
    </div>

    <div className="mt-3 flex justify-end gap-2">
      <Button variant="text" color="gray" onClick={onCancel} disabled={saving}>
        انصراف
      </Button>
      <Button color="blue" onClick={onSave} disabled={saving || !values.title?.trim()}>
        {saving ? "در حال ذخیره..." : "ذخیره"}
      </Button>
    </div>
  </div>
);

const ServiceStepsProgress = ({steps = []}) => {
  const enabledSteps = steps.filter((step) => step.status === "enable");
  const doneCount = enabledSteps.filter((step) => step.is_done).length;
  const totalCount = enabledSteps.length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const allDone = totalCount > 0 && doneCount === totalCount;

  if (totalCount === 0) {
    return (
      <div className="rounded-lg border border-blue-gray-50 bg-blue-gray-50/40 py-6 text-center text-sm text-blue-gray-400">
        هیچ مرحله‌ای برای این خدمت ثبت نشده است
      </div>
    );
  }

  return (
    <div className="relative z-0 rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50/50 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-blue-gray-800">
            {persianNumberFormat(doneCount, true)} از{" "}
            {persianNumberFormat(totalCount, true)} مرحله انجام شده
          </p>

          <p className="mt-1 text-xs text-blue-gray-500">
            وضعیت پیشرفت خدمت بر اساس مراحل فعال
          </p>
        </div>

        <div className="flex items-center gap-2">
          {allDone && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
              <BsCheckCircle className="h-3.5 w-3.5" />
              تکمیل شده
            </span>
          )}

          <span className="rounded-full bg-indigo-50 px-3 py-1 text-sm font-extrabold text-indigo-700 ring-1 ring-indigo-100">
            {persianNumberFormat(progressPct, true)}٪
          </span>
        </div>
      </div>

      <div className="overflow-x-auto overflow-y-visible px-1 pb-2 pt-5">
        <div
          dir="rtl"
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${totalCount}, minmax(86px, 1fr))`,
            minWidth: `${Math.max(totalCount * 92, 320)}px`,
          }}
        >
          {enabledSteps.map((step, index) => {
            const isCurrentStep =
              !step.is_done && (index === 0 || enabledSteps[index - 1]?.is_done);

            const isFutureStep = !step.is_done && !isCurrentStep;
            const shouldFillNextConnector = step.is_done && index < totalCount - 1;

            return (
              <div
                key={step.id || step.step || index}
                className="relative flex min-w-0 flex-col items-center px-1"
              >
                {index < totalCount - 1 && (
                  <div
                    className="absolute top-5 z-[1] h-1.5 -translate-y-1/2 rounded-full bg-slate-200"
                    style={{
                      right: "50%",
                      left: "-50%",
                    }}
                  >
                    <div
                      className="absolute inset-y-0 right-0 rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: shouldFillNextConnector ? "100%" : "0%",
                        background: allDone
                          ? "linear-gradient(90deg, #047857, #10b981)"
                          : "linear-gradient(90deg, #4f46e5, #7c3aed)",
                        boxShadow: shouldFillNextConnector
                          ? "0 1px 6px rgba(16, 185, 129, 0.28)"
                          : "none",
                      }}
                    />
                  </div>
                )}

                <div
                  className={[
                    "relative z-[5] flex h-10 w-10 items-center justify-center rounded-full text-xs font-extrabold transition-all duration-300",
                    step.is_done
                      ? "border-2 border-green-600 bg-emerald-600 text-white ring-4 ring-emerald-600 shadow-lg"
                      : isCurrentStep
                        ? "border-2 border-indigo-600 bg-white text-indigo-700 ring-4 ring-indigo-100 shadow-lg"
                        : "border border-slate-200 bg-white text-slate-400 shadow-sm",
                  ].join(" ")}
                  style={
                    step.is_done
                      ? {
                          background: "linear-gradient(135deg, #059669, #047857)",
                          boxShadow:
                            "0 8px 18px rgba(5, 150, 105, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.22)",
                        }
                      : isCurrentStep
                        ? {
                            boxShadow:
                              "0 8px 18px rgba(79, 70, 229, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
                          }
                        : undefined
                  }
                >
                  {isCurrentStep && (
                    <span className="absolute -inset-2 rounded-full bg-indigo-400/20 animate-ping" />
                  )}

                  <span className="relative z-10">
                    {step.is_done ? (
                      <BsCheckCircle className="h-4 w-4" />
                    ) : (
                      persianNumberFormat(step.step || index + 1, true)
                    )}
                  </span>
                </div>

                <div className="mt-2 w-full text-center">
                  <p
                    className={[
                      "truncate text-[11px] font-bold leading-5",
                      step.is_done
                        ? "text-emerald-700"
                        : isCurrentStep
                          ? "text-indigo-700"
                          : "text-blue-gray-400",
                    ].join(" ")}
                    title={step.title}
                  >
                    {step.title}
                  </p>

                  {step.is_done && step.updated_at && (
                    <p className="mt-0.5 truncate text-[9px] leading-4 text-blue-gray-400">
                      {step.updated_at}
                    </p>
                  )}

                  {isCurrentStep && (
                    <span className="mt-0.5 inline-flex items-center justify-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[9px] font-bold text-indigo-600">
                      <BsClock className="h-2.5 w-2.5" />
                      در حال انجام
                    </span>
                  )}

                  {isFutureStep && (
                    <span className="mt-0.5 inline-flex items-center justify-center rounded-full bg-slate-50 px-2 py-0.5 text-[9px] font-medium text-slate-400">
                      در انتظار
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export function ServiceReportManagement() {
  const [serviceReport, setServiceReport] = useState([]);
  const [gridRowCount, setGridRowCount] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState("id");
  const [sortType, setSortType] = useState(false);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState(gridCurrentPage * gridRowCount);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [supportId, setSupportId] = useState(null);
  const [supportName, setSupportName] = useState("");
  const [supportFamily, setSupportFamily] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [supportAssigning, setSupportAssigning] = useState(false);
  const [showSupportAssignment, setShowSupportAssignment] = useState(false);
  const [paymentLinkSending, setPaymentLinkSending] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [sentPaymentLink, setSentPaymentLink] = useState("");
  const debounceTimerRef = useRef(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const servicePayment = getServiceReportPaymentAmounts(selectedService);
  const servicePaymentAmount = servicePayment.amount;
  const canSendPaymentLink = Number.isFinite(servicePaymentAmount) && servicePaymentAmount > 0;
  const [serviceDocuments, setServiceDocuments] = useState([]);
  const [serviceDocumentsLoading, setServiceDocumentsLoading] = useState(false);
  const [serviceDocumentFormOpen, setServiceDocumentFormOpen] = useState(false);
  const [selectedServiceDocument, setSelectedServiceDocument] = useState(null);
  const [serviceDocumentSaving, setServiceDocumentSaving] = useState(false);
  const [serviceDocumentValues, setServiceDocumentValues] = useState({
    title: "",
    description: "",
    status: "new",
    file: null,
  });

  const [personId, setPersonId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [paidAtFrom, setPaidAtFrom] = useState("");
  const [paidAtTo, setPaidAtTo] = useState("");
  const [status, setStatus] = useState(null);
  const [minPrice, setMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);

  const handleRowClick = (service) => {
    setSelectedService(service);
    setServiceDocuments(service.service_documentations || service.serviceDocumentationList || []);
    setServiceDocumentFormOpen(false);
    setSelectedServiceDocument(null);
    setServiceDocumentValues({title: "", description: "", status: "new", file: null});
    setSupportId(service.support_id || null);
    setSupportName("");
    setSupportFamily("");
    setSupportPhone("");
    setShowSupportAssignment(false);
    setPaymentMethod(DEFAULT_PAYMENT_METHOD);
    setSentPaymentLink("");
    setOpenModal(true);
  };

  const closeServiceDocumentForm = () => {
    setServiceDocumentFormOpen(false);
    setSelectedServiceDocument(null);
    setServiceDocumentValues({title: "", description: "", status: "new", file: null});
  };

  const openNewServiceDocumentForm = () => {
    setSelectedServiceDocument(null);
    setServiceDocumentValues({title: "", description: "", status: "new", file: null});
    setServiceDocumentFormOpen(true);
  };

  const openEditServiceDocumentForm = (document) => {
    setSelectedServiceDocument(document);
    setServiceDocumentValues({
      title: document.title || "",
      description: document.description || "",
      status: document.status || "new",
      file: null,
    });
    setServiceDocumentFormOpen(true);
  };

  const refreshServiceDocuments = useCallback(async (serviceId) => {
    try {
      setServiceDocumentsLoading(true);
      const result = await getServicePaymentAndSteps(serviceId);

      if (result.status === 200) {
        const documents = result.data?.serviceDocumentationList || [];
        setServiceDocuments(documents);
      }
    } finally {
      setServiceDocumentsLoading(false);
    }
  }, []);

  const saveServiceDocument = async () => {
    if (!selectedService?.id || serviceDocumentSaving || !serviceDocumentValues.title?.trim()) {
      return;
    }

    try {
      setServiceDocumentSaving(true);

      const data = {
        service_id: selectedService.id,
        title: serviceDocumentValues.title,
        description: serviceDocumentValues.description,
        status: serviceDocumentValues.status,
        ...(serviceDocumentValues.file ? {file: serviceDocumentValues.file} : {}),
      };

      const result = selectedServiceDocument
        ? await updateAdminSupportServiceDocumentation({
            ...data,
            id: selectedServiceDocument.id,
          })
        : await submitAdminSupportServiceDocumentation(data);

      if (result.status === 200 || result.status === 201 || result.status === 202) {
        closeServiceDocumentForm();
        await refreshServiceDocuments(selectedService.id);
      }
    } catch (err) {
      Toaster.failure(
        null,
        <Typography variant="small">
          خطا در ذخیره مدرک خدمت
        </Typography>
      );
      console.error("Service document save error:", err);
    } finally {
      setServiceDocumentSaving(false);
    }
  };

  useEffect(() => {
    if (!openModal || !selectedService?.id) {
      return;
    }

    let ignore = false;

    async function getServiceDocuments() {
      try {
        if (!ignore) {
          await refreshServiceDocuments(selectedService.id);
        }
      } catch (err) {
        if (!ignore) {
          Toaster.failure(
            null,
            <Typography variant="small">
              خطا در دریافت مدارک خدمت
            </Typography>
          );
        }
        console.error("Service documents fetch error:", err);
      } finally {
      }
    }

    getServiceDocuments();

    return () => {
      ignore = true;
    };
  }, [openModal, selectedService?.id, refreshServiceDocuments]);

  useEffect(() => {
    if (gridCurrentPage * gridRowCount <= gridTotalRowCount) {
      setTo(gridCurrentPage * gridRowCount);
    } else {
      setTo(gridTotalRowCount);
    }

    if (gridTotalRowCount === 0) {
      setFrom(0);
    } else {
      setFrom((gridCurrentPage - 1) * gridRowCount + 1);
    }
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(data, paginate) {
    if (loading) return;

    try {
      setLoading(true);
      const result = await submitServiceReport(data, paginate);

      if (result.status === 200) {
        const {service_report, total_count} = result.data;
        setServiceReport(service_report || []);
        setGridTotalRowCount(total_count);
        return service_report || [];
      } else {
        Toaster.failure(
          null,
          <Typography variant="small">
            {result.data?.message || "خطا در دریافت گزارش"}
          </Typography>
        );
      }
    } catch (err) {
      Toaster.failure(
        null,
        <Typography variant="small">
          {err.message || "خطا در دریافت گزارش"}
        </Typography>
      );
      console.error("Service report fetch error:", err);
    } finally {
      setLoading(false);
    }
  }

  const filter = () => {
    const data = {};

    if (personId) {
      data.person_id = personId;
    }

    if (agentId) {
      data.agent_id = agentId;
    }

    if (fieldId) {
      data.field_id = fieldId;
    }

    if (branchId) {
      data.branch_id = branchId;
    }

    if (createdAtFrom) {
      data.created_at_from = createdAtFrom;
    }

    if (createdAtTo) {
      data.created_at_to = createdAtTo;
    }

    if (paidAtFrom) {
      data.paid_at_from = paidAtFrom;
    }

    if (paidAtTo) {
      data.paid_at_to = paidAtTo;
    }

    if (status) {
      data.status = status;
    }

    if (minPrice) {
      data.min_price = parseInt(minPrice);
    }

    if (maxPrice) {
      data.max_price = parseInt(maxPrice);
    }

    return data;
  };

  const clearFilters = () => {
    setPersonId(null);
    setAgentId(null);
    setFieldId(null);
    setBranchId(null);
    setCreatedAtFrom(null);
    setCreatedAtTo(null);
    setPaidAtFrom(null);
    setPaidAtTo(null);
    setStatus(null);
    setMinPrice(null);
    setMaxPrice(null);
  };

  const exportReport = async () => {
    try {
      setIsExporting(true);
      const data = filter();
      downloadFile("service_report_excel", data, "services");
    } catch (err) {
      Toaster.failure(
        null,
        <Typography variant="small">
          خطا در خروجی گرفتن
        </Typography>
      );
      console.error("Error exporting:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const assignSupport = async () => {
    if (!selectedService?.id || !supportId || supportAssigning) return;

    try {
      setSupportAssigning(true);

      const result = await updateServiceSupport({
        id: selectedService.id,
        support_id: supportId,
      });

      if (result.status === 202 || result.status === 200) {
        const updatedService = result.data?.service;
        const selectedSupportName = `${supportName || ""} ${supportFamily || ""}`.trim();

        const supportFields = {
          support_id: updatedService?.support_id || supportId,
          support_name:
            updatedService?.support_name ||
            selectedSupportName ||
            selectedService.support_name,
          support_phone:
            updatedService?.support_phone || supportPhone || selectedService.support_phone,
        };

        const nextService = {
          ...selectedService,
          ...updatedService,
          ...supportFields,
        };

        setSelectedService(nextService);

        setServiceReport((prevReport) =>
          prevReport.map((service) =>
            service.id === selectedService.id ? {...service, ...nextService} : service
          )
        );

        setShowSupportAssignment(false);
      }
    } catch (err) {
      console.error("Service support assignment error:", err);
    } finally {
      setSupportAssigning(false);
    }
  };

  const sendServicePaymentLink = async () => {
    if (!selectedService?.id || paymentLinkSending || !canSendPaymentLink) return;

    try {
      setPaymentLinkSending(true);
      setSentPaymentLink("");
      const result = await submitAdminServiceSendPaymentLink({
        service_id: selectedService.id,
        payment_method: paymentMethod,
      });

      if (result.status === 200) {
        setSentPaymentLink(result.data?.payment_link || "sent");
        // The API attaches the authenticated sender after the SMS succeeds.
        const refreshedServices = await getData(filter(), new URLSearchParams({
          sort_by: sortBy,
          sort_type: sortType === false ? "DESC" : "ASC",
          limit: parseInt(gridRowCount),
          offset: (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
        }).toString());
        const refreshedService = refreshedServices?.find(
          (service) => service.id === selectedService.id
        );
        if (refreshedService) setSelectedService(refreshedService);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">خطا در ارسال لینک پرداخت</Typography>);
      console.error("Service payment link error:", err);
    } finally {
      setPaymentLinkSending(false);
    }
  };

  const debouncedGetData = useCallback((data, paginate) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      getData(data, paginate);
    }, 500);
  }, []);

  useEffect(() => {
    const data = filter();

    const paginate = new URLSearchParams({
      sort_by: sortBy,
      sort_type: sortType === false ? "DESC" : "ASC",
      limit: parseInt(gridRowCount),
      offset: (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
    }).toString();

    if (gridCurrentPage !== 1 || sortBy !== "id" || sortType !== false) {
      getData(data, paginate);
    } else {
      debouncedGetData(data, paginate);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    personId,
    agentId,
    fieldId,
    branchId,
    createdAtFrom,
    createdAtTo,
    paidAtFrom,
    paidAtTo,
    status,
    minPrice,
    maxPrice,
    gridCurrentPage,
    gridRowCount,
    sortBy,
    sortType,
    debouncedGetData,
  ]);

  const renderServiceModalBody = () => {
    if (!selectedService) return null;

    const serviceStatus = getServiceStatusDisplay(selectedService.status);
    const paymentDisplay = getPaymentDisplay(selectedService.completed_full_payment);
    const serviceVatAmount = servicePayment.vat;
    const paymentLinkLabel = {
      first: "مبلغ مرحله اول",
      second: "مبلغ مرحله دوم",
      total: "مبلغ باقیمانده قابل پرداخت",
    }[servicePayment.type] || "مبلغ قابل پرداخت";
    const paymentLinkButtonLabel = {
      first: "ارسال لینک مرحله اول و پذیرش خدمت",
      second: "ارسال لینک مرحله دوم و پذیرش خدمت",
    }[servicePayment.type] || "ارسال لینک و پذیرش خدمت";
    const serviceTotalAmount = servicePayment.total;
    const serviceAmountWithoutVat = Math.max(serviceTotalAmount - serviceVatAmount, 0);
    const servicePaidAmount = servicePayment.paid;

    return (
      <div className="max-h-[38rem] overflow-y-auto rounded-xl bg-white pr-2">
        <div className="mb-5 flex flex-col gap-3 rounded-xl bg-blue-gray-50/60 px-4 py-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-bold text-blue-gray-900">جزئیات خدمت</h3>

              <span className="rounded-full bg-blue-gray-50 px-2 py-0.5 text-xs font-semibold text-blue-gray-500">
                #{persianNumberFormat(selectedService.id, true)}
              </span>
            </div>

            <p className="mt-1 text-sm text-blue-gray-500">
              {selectedService.person_name || "-"}
            </p>
          </div>

          <DetailBadge className={serviceStatus.className}>
            {serviceStatus.label}
          </DetailBadge>
        </div>

        <div className="space-y-6">
          <section className="space-y-3">
            <SectionTitle>مراحل انجام خدمت</SectionTitle>
            <ServiceStepsProgress steps={selectedService.service_steps || []} />
          </section>

          <section className="space-y-3 rounded-xl bg-white">
            <SectionTitle>خلاصه</SectionTitle>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoItem
                label="کاربر"
                value={selectedService.person_name}
                subValue={
                  selectedService.person_phone
                    ? persianNumberFormat(selectedService.person_phone)
                    : "-"
                }
              />

              <InfoItem
                label="مباشر"
                value={selectedService.agent_name}
                subValue={
                  selectedService.agent_phone
                    ? persianNumberFormat(selectedService.agent_phone)
                    : "-"
                }
              />

              <div className="self-start overflow-hidden rounded-xl border border-blue-gray-200 bg-white shadow-sm transition-all duration-200">
                <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        selectedService.support_name
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-blue-gray-100 text-blue-gray-500"
                      }`}
                    >
                      <BsPersonPlus className="h-4 w-4" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-medium text-blue-gray-500">پشتیبان</p>

                        {selectedService.support_name && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                            <span className="h-1 w-1 rounded-full bg-emerald-500" />
                            فعال
                          </span>
                        )}
                      </div>

                      <p className="mt-0.5 truncate text-sm font-semibold text-blue-gray-900">
                        {selectedService.support_name || "ثبت نشده"}
                      </p>

                      {selectedService.support_phone && (
                        <p className="text-xs text-gray-500">
                          {persianNumberFormat(selectedService.support_phone)}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowSupportAssignment((prevState) => !prevState)}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    {selectedService.support_name ? (
                      <BsPencilSquare className="h-3 w-3" />
                    ) : (
                      <BsPersonPlus className="h-3 w-3" />
                    )}

                    {selectedService.support_name ? "ویرایش" : "افزودن"}

                    {showSupportAssignment ? (
                      <BsChevronUp className="h-2.5 w-2.5" />
                    ) : (
                      <BsChevronDown className="h-2.5 w-2.5" />
                    )}
                  </button>
                </div>

                {showSupportAssignment && (
                  <div className="support-assign-panel border-t border-blue-gray-100 bg-blue-gray-50/50 px-3 py-3">
                    <div className="mb-2.5 flex items-center justify-between">
                      <p className="text-xs font-semibold text-blue-gray-700">
                        انتخاب پشتیبان
                      </p>

                      <button
                        type="button"
                        onClick={() => setShowSupportAssignment(false)}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="بستن انتخاب پشتیبان"
                      >
                        <BsX className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="space-y-2.5">
                      <SelectInput
                        label="پشتیبان"
                        hideLabel
                        defaultValue={supportId}
                        refreshSignal={supportId}
                        autoLoadService={getPersonsNameAndFamilyByRole}
                        autoLoadServiceData={SUPPORT_ROLE_ID}
                        autoLoadRender={({name, family, phone}) => {
                          const fullName = `${name?.trim()} ${family?.trim()}`.trim();
                          return phone
                            ? `${fullName} - ${persianNumberFormat(phone)}`
                            : fullName;
                        }}
                        selectStyles={{
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: "38px",
                            borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                            backgroundColor: "#ffffff",
                            transition: "border-color 160ms ease, box-shadow 160ms ease",
                            boxShadow: state.isFocused
                              ? "0 0 0 2px rgba(37, 99, 235, 0.15)"
                              : "none",
                            "&:hover": {borderColor: "#93c5fd"},
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: "#9ca3af",
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: "#6b7280",
                          }),
                          menu: (provided) => ({
                            ...provided,
                            border: "1px solid #e5e7eb",
                            animation: "supportSelectMenuIn 140ms ease-out",
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? "#dbeafe"
                              : state.isFocused
                                ? "#eff6ff"
                                : "white",
                            color: "#1f2937",
                            cursor: "pointer",
                            transition: "background-color 120ms ease",
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: "#1f2937",
                          }),
                        }}
                        onChange={setSupportId}
                        name
                        setName={setSupportName}
                        family
                        setFamily={setSupportFamily}
                        phone
                        setPhone={setSupportPhone}
                      />

                      <button
                        type="button"
                        disabled={!supportId || supportAssigning}
                        onClick={assignSupport}
                        className="h-9 w-full whitespace-nowrap rounded-lg bg-blue-600 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-gray-200 disabled:text-blue-gray-500"
                      >
                        {supportAssigning ? "در حال ثبت..." : "ثبت پشتیبان"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <InfoItem
                label="مبلغ"
                value={`${persianNumberFormat(selectedService.price, true)} تومان`}
                valueClassName="text-emerald-700"
              />
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>اطلاعات خدمت</SectionTitle>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoItem label="رشته" value={selectedService.field_title} />
              <InfoItem label="گرایش" value={selectedService.branch_title} />
              <InfoItem label="تاریخ پرداخت" value={selectedService.paid_at || "پرداخت نشده"} />
              <InfoItem label="توضیحات" value={selectedService.description || "-"} />
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>وضعیت و پرداخت</SectionTitle>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="border-r-2 border-blue-gray-100 px-3 py-1.5">
                <p className="mb-2 text-xs font-semibold text-blue-gray-500">
                  وضعیت خدمت
                </p>

                <DetailBadge className={serviceStatus.className}>
                  {serviceStatus.label}
                </DetailBadge>
              </div>

              <div className="border-r-2 border-blue-gray-100 px-3 py-1.5">
                <p className="mb-2 text-xs font-semibold text-blue-gray-500">
                  جزئیات پرداخت
                </p>

                {selectedService.status === "payment" ? (
                  <div className="space-y-2">
                    <DetailBadge className={paymentDisplay.className}>
                      <BsCreditCard className="ml-1 h-3.5 w-3.5" />
                      {paymentDisplay.label}
                    </DetailBadge>

                    {selectedService.completed_full_payment === false && (
                      <p className="text-xs text-amber-700">
                        منتظر پرداخت مرحله دوم
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-blue-gray-400">ثبت نشده</span>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100 bg-gradient-to-l from-blue-50 to-white px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm shadow-blue-200">
                    <BsCreditCard className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-blue-gray-900">ارسال لینک پرداخت</p>
                    <p className="mt-0.5 text-xs text-blue-gray-500">
                      لینک برای {selectedService.person_name || "کاربر"} پیامک می‌شود
                    </p>
                    {selectedService.multi_payment && (
                      <p className="mt-1 text-xs text-blue-700">پرداخت دو مرحله‌ای (۵۰٪ + ۵۰٪)</p>
                    )}
                  </div>
                </div>

                <span className="rounded-full border border-blue-100 bg-white px-2.5 py-1 text-xs font-semibold text-blue-700">
                  خدمت #{persianNumberFormat(selectedService.id, true)}
                </span>
              </div>

              <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_minmax(16rem,0.9fr)]">
                <div className="rounded-xl bg-blue-gray-50/70 p-3">
	                  <div className="flex items-center justify-between border-b border-dashed border-blue-gray-200 pb-2 text-xs">
	                    <span className="text-blue-gray-500">مبلغ خدمت بدون مالیات</span>
	                    <span className="font-semibold text-blue-gray-800">
	                      {persianNumberFormat(serviceAmountWithoutVat, true)} ریال
	                    </span>
	                  </div>
	                  <div className="flex items-center justify-between border-b border-dashed border-blue-gray-200 py-2 text-xs">
	                    <span className="text-blue-gray-500">مبلغ کل خدمت با مالیات</span>
                    <span className="font-semibold text-blue-gray-800">
                      {persianNumberFormat(serviceTotalAmount, true)} ریال
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-dashed border-blue-gray-200 py-2 text-xs">
                    <span className="text-blue-gray-500">
                      مبلغ پرداخت‌شده
                    </span>
                    <span className="font-semibold text-blue-gray-800">
                      {persianNumberFormat(servicePaidAmount, true)} ریال
                    </span>
                  </div>
                  <div className="flex items-end justify-between pt-3">
                    <span className="text-sm font-bold text-blue-gray-900">{paymentLinkLabel}</span>
                    <div className="text-left">
                      <span className="text-xl font-black text-blue-700">
                        {persianNumberFormat(canSendPaymentLink ? servicePaymentAmount : 0, true)}
                      </span>
                      <span className="mr-1 text-xs font-semibold text-blue-700">ریال</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-between gap-3">
                  <SelectInput
                    label="درگاه پرداخت"
                    defaultValue={paymentMethod}
                    refreshSignal={paymentMethod}
                    options={PAYMENT_GATEWAY_OPTIONS}
                    onChange={setPaymentMethod}
                  />

                  <Button
                    color="blue"
                    onClick={sendServicePaymentLink}
                    disabled={paymentLinkSending || !canSendPaymentLink}
                    loading={paymentLinkSending}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl text-xs shadow-md shadow-blue-100"
                  >
                    <BsCreditCard className="h-4 w-4" />
                    {paymentLinkSending ? "در حال ساخت و ارسال لینک..." : paymentLinkButtonLabel}
                  </Button>

                  <p className="text-[11px] leading-5 text-blue-gray-400">
                    با ارسال موفق لینک، این خدمت به‌صورت خودکار به شما تخصیص داده می‌شود.
                  </p>
                </div>
              </div>

              {sentPaymentLink && (
                <div className="mx-4 mb-4 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-emerald-800">
                  <BsCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold">لینک پرداخت با موفقیت ارسال شد</p>
                    {sentPaymentLink !== "sent" && (
                      <a
                        href={sentPaymentLink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block truncate text-xs underline decoration-emerald-300 underline-offset-2"
                      >
                        {sentPaymentLink}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SectionTitle>مدارک خدمت</SectionTitle>

              <Button
                size="sm"
                variant="outlined"
                className="flex items-center gap-2"
                onClick={openNewServiceDocumentForm}
              >
                <BsPlus className="h-4 w-4" />
                مدرک جدید
              </Button>
            </div>

            {serviceDocumentFormOpen && (
              <ServiceDocumentAdminForm
                document={selectedServiceDocument}
                values={serviceDocumentValues}
                setValues={setServiceDocumentValues}
                saving={serviceDocumentSaving}
                onSave={saveServiceDocument}
                onCancel={closeServiceDocumentForm}
              />
            )}

            <ServiceDocumentsView
              documents={serviceDocuments}
              loading={serviceDocumentsLoading}
              onEdit={openEditServiceDocumentForm}
            />
          </section>
        </div>
      </div>
    );
  };

  return (
    <PaginateContext.Provider
      value={{
        setSortBy: setSortBy,
        sortType: sortType,
        setSortType: setSortType,
        active: gridCurrentPage,
        setActive: setGridCurrentPage,
        totalRows: gridTotalRowCount,
        rowsCount: gridRowCount,
        setRowsCount: setGridRowCount,
        from: from,
        to: to,
      }}
    >
      <div className="flex w-full flex-col gap-4 p-4">
        <Card className="w-full pt-12">
          <CardHeader floated={true} shadow={false} className="w-full rounded-none">
            <div className="mb-6 flex items-center justify-between gap-8">
              <Typography variant="h5" color="blue-gray">
                گزارش خدمات
              </Typography>
            </div>
          </CardHeader>

          <CardBody>
            <ServiceReportFilterContainer
              personId={personId}
              setPersonId={setPersonId}
              agentId={agentId}
              setAgentId={setAgentId}
              fieldId={fieldId}
              setFieldId={setFieldId}
              branchId={branchId}
              setBranchId={setBranchId}
              status={status}
              setStatus={setStatus}
              minPrice={minPrice}
              setMinPrice={setMinPrice}
              maxPrice={maxPrice}
              setMaxPrice={setMaxPrice}
              createdAtFrom={createdAtFrom}
              setCreatedAtFrom={setCreatedAtFrom}
              createdAtTo={createdAtTo}
              setCreatedAtTo={setCreatedAtTo}
              paidAtFrom={paidAtFrom}
              setPaidAtFrom={setPaidAtFrom}
              paidAtTo={paidAtTo}
              setPaidAtTo={setPaidAtTo}
              loading={loading}
              hasRows={serviceReport.length > 0}
              isExporting={isExporting}
              onClear={clearFilters}
              onExport={exportReport}
            />
          </CardBody>
        </Card>

        <Card className="w-full">
          <CardBody className="w-full overflow-x-auto p-0">
            {loading && <LoadingOverlay />}

            <div className="relative">
              <ServiceReportGrid
                serviceReport={serviceReport}
                onRowClick={handleRowClick}
              />
            </div>
          </CardBody>

          <CardFooterPaginator />
        </Card>
      </div>

      {openModal && (
        <Modal
          open={openModal}
          setOpen={setOpenModal}
          size="xl"
          hideFooter
          children={{
            header: "جزئیات خدمت",
            body: renderServiceModalBody(),
          }}
        />
      )}
    </PaginateContext.Provider>
  );
}
